import type { ExperienceItem, ExperienceSequence } from "@/lib/types";

export interface RheaRecommendationContext {
  /** Detected traveler interests — sequence.interests. */
  interests:    string[];
  /** True when traveling with children (adults/children props). */
  hasChildren:  boolean;
  /** Explicit budget ceiling if the user has actually set one; null if unknown. */
  budgetMaxUsd: number | null;
}

/** Narrow, structural view of the page's active filters — decoupled from the
 *  route's own ActiveFilters type so this module has no dependency on it. */
export interface RheaRecommendationFilterContext {
  categories: Set<string>;
  timeOfDay:  string | null;
  reservable: boolean;
}

const FAMILY_TAG_PATTERN = /family|kids|ni[ñn]os?|familiar/i;

// ─── Recommendation Badge Coherence V1 ─────────────────────────────────────
// Production audit: "Madrid Segway tour" ranking improved (Pub Crawl bottomed
// out instead of winning #1), but the item could still surface with a
// "Recomendada por RHEA" badge — the score-based badge logic had no concept
// of explicit activity-intent incompatibility. Mirrors (does not import —
// this is a display-only frontend concern, same convention as
// isExcursionItem's own title-heuristic in marketplaceInventory.ts) the
// backend's ACTIVITY_FAMILY_KEYWORDS / detect_activity_intent /
// _item_activity_families / _activity_intent_adjustment in
// engine/experiences_engine.py. Deterministic, no LLM, never city-specific.
const ACTIVITY_FAMILY_KEYWORDS: Readonly<Record<string, readonly string[]>> = {
  segway:    ["segway"],
  scooter:   ["scooter", "e-scooter", "escooter", "tuk tuk", "tuk-tuk", "tuktuk", "sidecar"],
  bike:      ["bike", "bicicleta", "e-bike", "ebike", "bici "],
  tapas:     ["tapas", "food tour", "mercado", "market tour"],
  cooking:   ["cooking class", "cooking workshop", "paella", "clase de cocina"],
  flamenco:  ["flamenco", "tablao"],
  stadium:   ["stadium", "estadio", "bernabéu", "bernabeu", "real madrid", "camp nou", "santiago bernabéu"],
  pub_crawl: ["pub crawl", "pub-crawl", "bar crawl", "bar-crawl", "fiesta", "nightlife", "discoteca", "clubbing"],
};

const PUB_CRAWL_CATEGORY_SIGNAL = new Set(["nightlife"]);

function textContainsAny(haystack: string, needles: readonly string[]): boolean {
  return needles.some(n => haystack.includes(n));
}

/** Returns the activity families the QUERY names, or an empty set for a
 *  generic query ("Madrid", "Madrid experiencias"). */
export function detectActivityIntentFamilies(query: string | null | undefined): Set<string> {
  const q = (query ?? "").trim().toLowerCase();
  if (!q) return new Set();
  const families = new Set<string>();
  for (const [family, keywords] of Object.entries(ACTIVITY_FAMILY_KEYWORDS)) {
    if (textContainsAny(q, keywords)) families.add(family);
  }
  return families;
}

/** Returns the activity families this ITEM itself belongs to (title,
 *  category, tags) — zero, one, or more than one. category="nightlife" is
 *  folded into pub_crawl even without the literal words, same as backend.
 *
 *  `includePubCrawlTags` (default true, so badge/ranking behavior is
 *  unchanged): whether the item's `tags` array also counts toward the
 *  pub_crawl fold, not just `category`. Geo & Media Relevance Hardening V1 —
 *  real Viator items (e.g. a Bernabéu/stadium tour) can carry a secondary,
 *  noisy "nightlife" TAG alongside an unambiguous stadium/segway/etc TITLE
 *  even though `category` itself is something else entirely — `tags` is a
 *  multi-value, less authoritative field than `category`, so
 *  mediaRelevance.ts passes `false` here specifically to avoid discarding a
 *  perfectly good, valid real photo over that noise. Badge/ranking callers
 *  keep the default (true) — that surface already tolerates a false
 *  positive better (a missed "good" badge) than media relevance does
 *  (needlessly discarding a valid real photo). */
export function getItemActivityFamilies(
  exp: ExperienceItem,
  options?: { includePubCrawlTags?: boolean },
): Set<string> {
  const includePubCrawlTags = options?.includePubCrawlTags ?? true;
  const tags = (exp.tags ?? []).map(t => t.toLowerCase());
  const category = (exp.category ?? "").toLowerCase();
  const baseText = `${exp.title ?? ""} ${category}`.toLowerCase();
  const tagText = tags.join(" ");
  const families = new Set<string>();
  for (const [family, keywords] of Object.entries(ACTIVITY_FAMILY_KEYWORDS)) {
    // Preserve the old/default behavior for all non-nightlife families:
    // tags can still identify segway/bike/etc. Only pub_crawl is gated,
    // because noisy secondary "nightlife" tags were causing media relevance
    // to discard valid real photos for stadium/culture products.
    const text = family === "pub_crawl" && !includePubCrawlTags
      ? baseText
      : `${baseText} ${tagText}`;
    if (textContainsAny(text, keywords)) families.add(family);
  }
  const tagsSignal = includePubCrawlTags && tags.some(t => PUB_CRAWL_CATEGORY_SIGNAL.has(t));
  if (PUB_CRAWL_CATEGORY_SIGNAL.has(category) || tagsSignal) {
    families.add("pub_crawl");
  }
  return families;
}

/**
 * True when the query names a specific activity AND this item is
 * confidently classified into a DIFFERENT specific family — mirrors the
 * backend's _activity_intent_adjustment(exp, query) > 0 case. Display-only:
 * never reorders or hides the item (ranking already handles that
 * server-side) — only gates whether it's allowed to carry a "top"/"good"
 * recommendation badge, since recommending an item the user's own query
 * just excluded is dishonest regardless of how well it otherwise scores.
 */
export function hasActivityIntentConflict(exp: ExperienceItem, query: string | null | undefined): boolean {
  const queryFamilies = detectActivityIntentFamilies(query);
  if (queryFamilies.size === 0) return false;
  const itemFamilies = getItemActivityFamilies(exp);
  if (itemFamilies.size === 0) return false;
  for (const family of itemFamilies) {
    if (queryFamilies.has(family)) return false;
  }
  return true;
}

/**
 * Pure scoring heuristic for the "RHEA recommends" marketplace badge.
 *
 * Reads only fields already present on ExperienceItem/ExperienceSequence —
 * never invents a rating, review, or availability signal. The result is a
 * relative ranking score, not a literal rating; it is never rendered as a
 * number, only used to decide which 1–3 rows (if any) earn a badge.
 */
export function getRheaRecommendationRank(
  exp:      ExperienceItem,
  context:  RheaRecommendationContext,
  filters:  RheaRecommendationFilterContext,
  sequence: ExperienceSequence,
): number {
  let score = 0;
  const isProvider = exp.source_type === "provider";

  // Provider-backed gets a modest boost — real, bookable supply. Kept small
  // and gated behind genuine quality signals so a bare/generic provider row
  // can't outrank a strong local match purely by virtue of "being a provider".
  if (isProvider) {
    score += 4;
    if (exp.availability_confirmed === true) score += 5;
    if (exp.source_class === "LIVE_PROVIDER") score += 2;
    if (typeof exp.supplier_intelligence_score === "number") {
      // Real backend quality signal (0–1) scales the boost instead of adding
      // a flat bonus — this is exactly what keeps low-quality/generic
      // provider rows from riding the coattails of "is a provider".
      score += exp.supplier_intelligence_score * 8;
    }
  }

  // Category match with the interests detected from the user's query.
  if (context.interests.includes(exp.category)) score += 10;

  // The user is actively filtering within a category — recommend more
  // strongly inside what they're already browsing.
  if (filters.categories.size > 0 && filters.categories.has(exp.category)) score += 6;

  // Reservable-now filter active and this item actually satisfies it.
  if (filters.reservable && exp.availability_confirmed === true) score += 4;

  // Time-of-day relevance: matches the active filter, or simply has a
  // specific (non-"any") recommended time, which is more decision-useful
  // than a vague "anytime" slot.
  if (filters.timeOfDay && exp.time_of_day === filters.timeOfDay) score += 5;
  else if (exp.time_of_day && exp.time_of_day !== "any") score += 1;

  // Real rating/review signal — Bayesian-shrunk toward a neutral prior when
  // review evidence is thin, the same principle the backend's own quality
  // score uses (CLAUDE.md Regla 23: "mientras menos evidencia tengas, más
  // debes confiar en el promedio global") — a single 5.0 with 1 review must
  // not outrank a well-reviewed 4.6. Reads only the real rating/review_count
  // fields already on the item; never invents either.
  if (typeof exp.rating === "number" && exp.rating > 0) {
    const reviewCount = typeof exp.review_count === "number" && exp.review_count > 0 ? exp.review_count : 0;
    const PRIOR_WEIGHT = 10;
    const PRIOR_MEAN   = 4.0;
    const bayesian = (PRIOR_WEIGHT * PRIOR_MEAN + exp.rating * reviewCount) / (PRIOR_WEIGHT + reviewCount);
    // Centered on the prior so a below-average bayesian score can pull the
    // rank down, not just boost above-average ones — scaled to roughly
    // match this function's other signal magnitudes (a strong, well-
    // reviewed rating is worth about as much as the provider/availability
    // boosts above).
    score += (bayesian - PRIOR_MEAN) * 6;
  }

  // Price availability + budget fit — a known price is more useful than an
  // unknown one; staying under an explicit budget ceiling is a plus.
  if (typeof exp.price_from === "number") {
    score += 2;
    if (context.budgetMaxUsd != null && exp.price_from <= context.budgetMaxUsd) score += 2;
  }

  // Duration fit — a real, plannable duration reads as a genuine activity
  // rather than a vague catalog stub.
  if (exp.duration_hours > 0) {
    score += 1;
    if (exp.duration_hours >= 0.5 && exp.duration_hours <= 6) score += 1;
  }

  // Family-friendly signal — only checked when traveling with children.
  if (context.hasChildren) {
    const tags = exp.tags ?? [];
    if (tags.some(t => FAMILY_TAG_PATTERN.test(t))) score += 5;
    if (exp.category === "nightlife") score -= 4;
  }

  // Destination relevance — a populated, specific neighborhood is a stronger
  // real-world signal than a missing/generic one.
  if (sequence.city_display && exp.neighborhood.trim().length > 0) score += 1;

  return score;
}
